import { message } from "antd"
import {
  withPointBalanceRefresh,
  runPointAction,
} from "~/common/billing/pointAction"
import { getVideoAnalysisPointCost } from "~/common/billing/pointCosts"
import { reconcileImageMentionLabels } from "~/common/media/resourceMentions"
import { getVideoUrlDuration } from "~/common/media/uploadedResources"
import { buildRecreateProductInfo } from "~/common/video/recreateProductInfo"
import { getVideoGenerationRequestBodySizeError } from "~/common/video/videoGenerationRequest"
import {
  buildStoryboardPromptText,
  buildStoryboardPromptTextFromSource,
  parseStoryboardPayload,
} from "~/common/video/storyboard"
import {
  isTaskStepFailedStatus,
  isTaskStepProcessingStatus,
} from "~/common/constants"
import { pollHotVideoStep } from "~/common/tasks/hotVideoTask"
import {
  getRecreateDurationError,
  getRecreateInputError,
  getRecreateGenerationResources,
  getRecreateReferenceSettings,
  getRecreateImageUrls,
  RECREATE_IMAGE_LIMIT,
} from "~/common/video/recreate"
import { getHotVideoDetail, parseAndOptimizeHotVideoPrompt } from "~/api/task"

import type { HotVideoState, HotVideoSetState, HotVideoGetState } from "./types"
import {
  requestGuard,
  promptSaver,
  getPromptState,
  getInputFingerprint,
} from "./shared"

export const createScriptActions = (
  set: HotVideoSetState,
  get: HotVideoGetState,
): Pick<
  HotVideoState,
  | "updateReplicaStoryboardRows"
  | "updateRawModelOutput"
  | "reconcileImageMentions"
  | "analyzeAndOptimizePrompt"
> => ({
  updateReplicaStoryboardRows: (rows) => {
    if (
      get().isRequesting ||
      get().isRestoringTaskDraft ||
      get().isSubmittingVideoTask
    )
      return
    const { replicaPrompt, replicaStoryboardRows, hasEditedScript } = get()
    if (!rows.length) {
      void message.error("至少需保留一个分镜")
      return
    }
    const payload = parseStoryboardPayload(replicaPrompt)
    set({
      replicaStoryboardRows: rows,
      hasEditedScript:
        hasEditedScript ||
        JSON.stringify(rows) !== JSON.stringify(replicaStoryboardRows),
      ...(payload
        ? {
            replicaPrompt:
              buildStoryboardPromptTextFromSource(replicaPrompt, rows) ||
              buildStoryboardPromptText(payload, rows),
          }
        : {}),
    })
    promptSaver.update(get().replicaPrompt)
  },

  updateRawModelOutput: (value) => {
    if (
      get().isRequesting ||
      get().isRestoringTaskDraft ||
      get().isSubmittingVideoTask
    )
      return
    set((state) => ({
      hasEditedScript: state.hasEditedScript || value !== state.rawModelOutput,
      ...getPromptState(value),
    }))
    promptSaver.update(value)
  },

  reconcileImageMentions: (
    previousUrls,
    nextUrls,
    preserveReplacedPositions,
  ) => {
    const state = get()
    const { replicaPrompt } = state
    const imageUrls = (urls: string[]) =>
      getRecreateGenerationResources({
        ...state,
        videoImageUrls: urls.join("\n"),
      })
        .filter((resource) => resource.type?.startsWith("image/"))
        .map((resource) => resource.url)
    const nextPrompt = reconcileImageMentionLabels(
      replicaPrompt,
      imageUrls(previousUrls),
      imageUrls(nextUrls),
      { preserveReplacedPositions },
    ).value
    if (nextPrompt !== replicaPrompt) {
      set(getPromptState(nextPrompt))
      promptSaver.update(get().replicaPrompt)
      void promptSaver.flush()
    }
  },
  analyzeAndOptimizePrompt: async ({ onSubmitted } = {}) => {
    const state = get()
    if (state.isAnalyzingProduct) {
      void message.warning("商品信息正在生成中，请稍候")
      return null
    }
    const {
      backgroundMusicMode,
      videoUrl,
      isRequesting,
      isRestoringTaskDraft,
    } = state
    const videoReferenceImageUrls = getRecreateImageUrls(state.videoImageUrls)
    const videoGenerationResources = videoReferenceImageUrls.map((url) => ({
      url,
    }))
    const hasReferenceImages = videoReferenceImageUrls.length > 0
    const hasSourceVideo = Boolean(videoUrl.trim())
    const inputFingerprint = getInputFingerprint(state)
    if (isRequesting || isRestoringTaskDraft) return null
    const inputError = getRecreateInputError(state)
    if (inputError) {
      void message.warning(inputError)
      return null
    }
    if (!hasReferenceImages && !hasSourceVideo) {
      void message.warning("检测到商品图、参考视频未上传，请补齐素材后继续")
      return null
    }

    if (!hasReferenceImages) {
      void message.warning("检测到还未上传商品图，请上传商品图后再继续")
      return null
    }

    if (!hasSourceVideo) {
      void message.warning("检测到还未上传参考视频，请上传参考视频后再继续")
      return null
    }

    if (videoReferenceImageUrls.length > RECREATE_IMAGE_LIMIT) {
      void message.warning(
        `最多支持 ${RECREATE_IMAGE_LIMIT} 张新参考图，请调整素材后再生成脚本`,
      )
      return null
    }

    const draftRevision = state.draftRevision
    const isStale = requestGuard.begin(
      () => get().draftRevision !== draftRevision,
    )
    const trimmedVideoUrl = videoUrl.trim()

    set({ isRequesting: true, status: "视频校验中..." })

    try {
      const sourceVideoDuration = await getVideoUrlDuration(trimmedVideoUrl)
      if (isStale()) return null
      const start_time = state.start_time
      const end_time = state.end_time || sourceVideoDuration
      const duration = end_time - start_time
      const durationError =
        getRecreateDurationError(duration) ||
        (!Number.isFinite(start_time) ||
        start_time < 0 ||
        end_time > sourceVideoDuration
          ? "截取范围超出参考视频，请重新选择视频片段"
          : "")

      if (durationError) {
        set({ status: "视频校验失败" })
        void message.warning(durationError)
        return null
      }

      set({ status: "" })
      const submission = await runPointAction(
        getVideoAnalysisPointCost(duration),
        async () => {
          set({ status: "素材校验中..." })
          const requestBodySizeError =
            await getVideoGenerationRequestBodySizeError(
              { product_info: buildRecreateProductInfo(state.productInfo) },
              videoGenerationResources,
            )

          if (isStale()) return null
          if (requestBodySizeError) {
            set({ status: "素材校验失败" })
            void message.warning(requestBodySizeError)
            return null
          }

          if (get().resourcesPending) {
            set({ status: "" })
            return null
          }
          if (!(await get().saveReplicaPrompt()) || isStale()) return null
          get().resetScript()
          set({ status: "AI拆解融合中..." })

          const currentTaskId = await get().ensureTaskId()

          if (isStale() || !currentTaskId) return null
          if (get().resourcesPending) {
            set({ status: "" })
            return null
          }

          const referenceSettings = getRecreateReferenceSettings({
            ...state,
            start_time,
            end_time,
          })
          const response = await parseAndOptimizeHotVideoPrompt({
            task_id: currentTaskId,
            product_info: buildRecreateProductInfo(state.productInfo),
            video_url: trimmedVideoUrl,
            ...(start_time > 0 || end_time !== sourceVideoDuration
              ? { start_time, end_time }
              : {}),
            model_figure: referenceSettings.model_figure,
            timbre_url: referenceSettings.timbre_url,
            duration: Math.ceil(duration),
            image_urls: videoReferenceImageUrls,
          })
          return { response, currentTaskId, referenceSettings }
        },
        { isStale: () => isStale() || get().resourcesPending },
      )
      if (!submission) return null
      const { response, currentTaskId, referenceSettings } = submission
      if (isStale()) return null
      const optimizePrompt = response.data?.optimize_prompt

      if (!response.data) {
        throw new Error("创建拆解融合任务失败")
      }

      if (Object.values(state.productInfo).some((value) => value.trim())) {
        onSubmitted?.("填写商品信息")
      }
      if (referenceSettings.timbre_url) {
        onSubmitted?.("指定口播音色")
      }
      onSubmitted?.(
        backgroundMusicMode === "original"
          ? "延用原视频BGM"
          : "AI自动生成新BGM",
      )

      const finishedPrompt = optimizePrompt?.result_prompt
        ? optimizePrompt
        : await withPointBalanceRefresh(() =>
            pollHotVideoStep<API.Task.OptimizePromptInfo>({
              taskId: currentTaskId,
              isStale,
              selectStep: (task) => task.optimize_prompt,
              isProcessing: (step) => isTaskStepProcessingStatus(step.status),
              isFailed: (step) => isTaskStepFailedStatus(step.status),
              getErrorMessage: (step) => step.error_message || "构建提示语失败",
              timeoutMessage: "构建提示语超时，请稍后重试",
            }),
          )
      if (isStale()) return null
      const output = finishedPrompt?.result_prompt || ""
      if (!output.trim()) throw new Error("未获取到有效的视频脚本，请重试")
      let sourcePromptOutput = response.data.parse_video?.result_text || ""

      if (!sourcePromptOutput) {
        try {
          const taskDetail = await getHotVideoDetail({
            task_id: currentTaskId,
          })

          sourcePromptOutput = taskDetail.data?.parse_video?.result_text || ""
        } catch {
          // 原视频脚本只用于辅助查看，不影响融合脚本的主流程。
        }
      }

      if (isStale()) return null
      set({
        ...getPromptState(output),
        staffPreview: null,
        canSaveScript: true,
        sourcePrompt: sourcePromptOutput,
        replicaPromptInputFingerprint: inputFingerprint,
        savedReferenceSettings: referenceSettings,
        savedReferenceModel: state.referenceModel,
        start_time,
        end_time,
        status: "拆解融合完成",
      })
      promptSaver.reset(currentTaskId, get().replicaPrompt)
      return output
    } catch (error) {
      if (isStale()) return null
      const errorMessage =
        error instanceof Error ? error.message : "拆解融合失败，请稍后重试"

      set({ status: "拆解融合失败", taskErrorMessage: errorMessage })
      void message.error(errorMessage)
      return null
    } finally {
      if (!isStale()) set({ isRequesting: false })
    }
  },
})
